import { Row, Space, Tag, Tooltip } from "antd";
import {
  ScrollableTable,
  FilterConfig,
  TableColumnProps,
  ActiveFilters,
} from "Components/ScrollableTable/ScrollableTable";
import { SortDirectionType, SortDirection } from "react-virtualized";
import { Button } from "Components/General/Button";
import { I18N_NS } from "Config/i18n";
import { observer } from "mobx-react";
import { ViewsActionsMenu } from "../Components/ViewsActionsMenu";
import { viewsStore } from "../Stores/ViewsStore";
import { dayjs } from "Config/dayjs";
import { fileDetailsStore } from "Components/FileDetails/FileDetailsStore";
import { ViewsTableRowActions } from "../Components/ViewsTableRowActions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ViewSettings } from "../Components/ViewSettings";
import { ViewDateFilter } from "../Components/ViewDateFilter";
import { ViewSearchInputFilter } from "../Components/ViewSearchInputFilter";
import { SaveFiltersButton } from "../Components/SaveFiltersButton";
import { fileUploadStore } from "Pages/Project/Documents/FileUpload/FileUploadStore";
import { DocumentsDnD } from "Pages/Project/Documents/Components/DocumentsDnD";
import { projectsDataStore, userInfoStore } from "Stores";
import { getFiltersForTable } from "utils/libHelpers/getFiltersForTable";
import { ViewFilterSearchBar } from "../Components/ViewFilterSearchBar";
import { DirectDownload } from "Pages/Project/Documents/Components/DirectDownload";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// extend metadata with key to show in dropdown list
interface ViewUserSettingsShowMetadataWithKey extends API_ViewUserSettingsShowMetadata {
  key?: string;
}

export interface ViewUserSettingsWithKey extends API_ViewUserSettings {
  showMetadata?: ViewUserSettingsShowMetadataWithKey[];
}

interface ViewProps {
  view: API_ViewInformation;
}

export interface ViewUIFilters {
  [namespace: string]: string[];
}

const viewSortByMap: { [key in keyof API_ViewInformation]: API_VIEWREQUEST_SORTBY } = {
  name: "FILE_NAME",
  index: "INDEX",
  created: "UPDATED",
  description: "DESCRIPTION",
  type: "TYPE",
};

export const View = observer(({ view }: ViewProps) => {
  const { t } = useTranslation(I18N_NS.PROJECT);
  const table = useRef<ScrollableTable>(null);
  // Namespace sort settings per view so a metadata sort from one view is not applied to another.
  const sortingNamespace = `views_${view.id}`;
  const sortingSettings = userInfoStore.getSortingSettings(sortingNamespace);
  const [sortBy, setSortBy] = useState<string>(sortingSettings?.sortBy || "name");
  const [sortDirection, setSortDirection] = useState<SortDirectionType>(
    (sortingSettings?.direction.toUpperCase() as SortDirectionType) || SortDirection.ASC
  );
  const [data, setData] = useState<API_ViewInformation[]>([]);
  const [availableFilters, setAvailableFilters] = useState<{ [key: string]: FilterConfig[] }>({});
  const [activeFilters, setActiveFilters] = useState<ViewUIFilters>({});
  const [allFilters, setAllFilters] = useState<ActiveFilters>({});
  const [viewAll, setViewAll] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [selectedFileIds, setSelectedFileIds] = useState<number[]>([]);
  const [filter, setFilter] = useState("");
  const [youngerThanDays, setYoungerThanDays] = useState("");
  const [highlightedFileId, setHighlightedFileId] = useState<number | null>(viewsStore.navigateToFileId ?? null);

  const onFilterChange = useCallback((value: string) => {
    setFilter(value);
  }, []);

  const onExternalFilterViewEvent = async (e: CustomEvent<API_ViewRequest["metadata"]>) => {
    await table.current?.manageFilters(e.detail);
    table.current?.filter();
  };

  const fetchData = useCallback(
    async (fromSavedFilters = false) => {
      try {
        setLoading(true);
        if (viewsStore.currentViewId !== viewsStore.viewInformation?.id) {
          await viewsStore.fetchFilters();
        }
        await defineMetaFilters(viewsStore.viewInformation.metadata);
        const params = getRequestFormBody();
        await viewsStore.searchFilesByFilter(params, fromSavedFilters);
        setData(viewsStore.files);
      } catch (error) {
        console.error("Error fetching data:", error);
        setData([]);
      } finally {
        setLoading(false);
      }
    },
    [sortBy, sortDirection, activeFilters, viewAll, youngerThanDays, filter]
  );

  const selectAll = useCallback(async () => {
    try {
      const params = getRequestFormBody();
      const response: API_ViewInformation[] = await viewsStore.selectAll(view.id, params);
      setSelectedFileIds(response.map((file) => file.id));
      return response;
    } catch (error) {
      throw error;
    }
  }, [sortBy, sortDirection, activeFilters, viewAll, youngerThanDays, filter, view.id]);

  const handleFilter = useCallback(
    (newActiveFilters: ViewUIFilters) => {
      const isFiltersEmpty = Object.keys(newActiveFilters).length === 0;
      const filtersChanged = JSON.stringify(newActiveFilters) !== JSON.stringify(activeFilters);

      setViewAll(isFiltersEmpty);
      setActiveFilters(isFiltersEmpty ? {} : newActiveFilters);

      if (filtersChanged || isFiltersEmpty) {
        table.current?.scrollToTop();
      }
    },
    [activeFilters]
  );

  const handleYoungerThanDaysChange = useCallback((value: string) => {
    setYoungerThanDays(value);
  }, []);

  const handleExternalFilter = useCallback(
    async (newActiveFilters: ViewUIFilters) => {
      setActiveFilters(newActiveFilters);
      setViewAll(false);

      await table.current?.manageFilters(newActiveFilters);
      table.current?.filter();
    },
    [fetchData]
  );

  const handleSort = useCallback(
    (newSortBy: keyof API_ViewInformation, newSortDirection: SortDirectionType) => {
      const sortKey = newSortBy as string;
      const shallReFetch = sortKey !== sortBy || newSortDirection !== sortDirection;

      if (shallReFetch) {
        setSortBy(sortKey);
        setSortDirection(newSortDirection);
        table.current?.scrollToTop();
        userInfoStore.storeSortingSettings(sortingNamespace, {
          sortBy: sortKey,
          direction: newSortDirection.toLowerCase() as "asc" | "desc",
        });
      }
    },
    [sortBy, sortDirection, sortingNamespace]
  );

  const handleSelectRows = useCallback(({ selectedRowKeys }) => {
    setSelectedFileIds(selectedRowKeys);
  }, []);

  const resetSelection = useCallback(() => {
    setSelectedFileIds([]);
    setHighlightedFileId(null);
  }, []);

  const defineMetaFilters = useCallback(async (metadata: API_Metadata[]) => {
    await new Promise<void>((res, rej) => {
      try {
        const availableFilters = {};
        const allFilters = {};
        metadata.forEach((element) => {
          const key = `metadata_${element.key}`;
          if (element.rangeList.length) {
            availableFilters[key] = element.rangeList.map(({ value, description }) => ({
              text: value,
              value,
              description,
            }));
            allFilters[key] = ["*"];
          } else if (element.format === "*") {
            allFilters[key] = ["*"];
          }
        });
        setAvailableFilters(availableFilters);
        setAllFilters(allFilters);
        res();
      } catch (error) {
        rej(error);
      }
    });
  }, []);

  const getRequestFormBody = () => {
    const params: API_ViewRequest = {};

    const filtersToSet =
      viewAll || (Object.keys(activeFilters).length === 0 && filter !== "") ? allFilters : activeFilters;

    const { created, type: fileType, fileState, ...metadataAndLabelFilters } = filtersToSet;

    if (metadataAndLabelFilters) {
      const { metadataFilters, typesAndLabels } = Object.keys(metadataAndLabelFilters).reduce(
        (acc, key) => {
          if (key.startsWith("metadata_")) {
            acc.metadataFilters[key.replace("metadata_", "")] = metadataAndLabelFilters[key];
          } else if (key.startsWith("labelType_")) {
            acc.typesAndLabels[key.replace("labelType_", "")] = metadataAndLabelFilters[key].map((i) => +i);
          }
          return acc;
        },
        { metadataFilters: {}, typesAndLabels: {} }
      );

      params.metadata = Object.keys(metadataFilters).length
        ? metadataFilters
        : (() => {
            const { created, type, fileState, ...fallbackMetadata } = allFilters;
            return fallbackMetadata;
          })();

      if (Object.keys(typesAndLabels).length) {
        params.typesAndLabels = typesAndLabels;
      }
    }

    if (sortBy.startsWith("metadata_")) {
      const metadataSortName = sortBy.replace("metadata_", "");
      const metadataExistsInView = viewsStore.viewInformation.metadata?.some((meta) => meta.key === metadataSortName);
      // Sorting settings are stored globally for all views, so a metadata sort from a
      // previous view may not exist in the current one. Fall back to the default sort
      // instead of sending an invalid metadataSortName (backend responds with 400).
      if (metadataExistsInView) {
        params.sortBy = "METADATA";
        params.metadataSortName = metadataSortName;
      } else {
        params.sortBy = viewSortByMap.name;
      }
    } else {
      params.sortBy = viewSortByMap[sortBy];
    }
    params.sortOrder = sortDirection;
    params.nameAndDescriptionFilter = filter;
    params.created = created as string[];
    params.type = fileType as string[];
    params.fileStatus = fileState?.map((i) => +i);
    params.youngerThanDays = youngerThanDays;

    return params;
  };

  const handleRowClick = useCallback(({ rowData }: { rowData: API_ViewInformation }) => {
    if (rowData?.id) {
      if (highlightedFileId !== rowData.id) {
        setHighlightedFileId(rowData.id);
      }
      fileDetailsStore.initModal(rowData.parentId, rowData.id);
    }
  }, []);

  const handleUpdate = async () => {
    try {
      setLoading(true);
      await Promise.all([viewsStore.fetch(), viewsStore.fetchFilters()]);
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const getValuableFilters = (key: string): FilterConfig[] => {
    const metadataKey = `metadata_${key}`;
    return availableFilters[metadataKey] || [];
  };

  const openViewsFileUpload = () => {
    fileUploadStore.initModal({ module: "VIEW" });
  };

  const getRowClassName = (record: API_ViewInformation) => classNames({ unread: record?.unread });

  const createMetadataColumn = (meta: API_Metadata, isGrow: number): TableColumnProps<API_ViewInformation> => {
    const baseColumn = {
      dataKey: `metadata_${meta.key}` as keyof API_ViewInformation,
      label: meta.key,
      width: 170,
      flexGrow: isGrow,
      headerClassName: "views-meta-header",
      cellRenderer: ({ rowData, dataKey }) => {
        const values = rowData.metadata?.filter(({ key }) => key === dataKey.replace("metadata_", ""));
        if (!values?.length) return "";
        return values.map(({ value, valueDescription }) => (
          <Tooltip key={value} title={valueDescription}>
            {value}
          </Tooltip>
        ));
      },
    };

    if (meta.format.toLowerCase() === "w") {
      return {
        ...baseColumn,
        filters: getValuableFilters(meta.key),
        filterPredicate: (filter, value) => filter === `${value}`,
      };
    } else if (meta.format === "*") {
      return { ...baseColumn, customFilterRender: (props) => <ViewSearchInputFilter {...props} /> };
    } else if (meta.format === "DATE") {
      return { ...baseColumn, customFilterRender: (props) => <ViewDateFilter {...props} /> };
    }

    return baseColumn;
  };

  const getTypeLabelColumns = (
    typesWithLabels: API_DataType[] | undefined,
    isGrow: number
  ): TableColumnProps<API_ViewInformation>[] => {
    if (!typesWithLabels) return [];
    return typesWithLabels
      .filter((type) => type.show)
      .map((type) => ({
        dataKey: `labelType_${type.id}` as keyof API_ViewInformation,
        label: type.viewName,
        width: 170,
        flexGrow: isGrow,
        disableSort: true,
        headerClassName: "views-meta-header",
        cellRenderer: ({ rowData, dataKey }) => {
          const currentType = rowData.typesWithLabels?.find(
            (item) => item.id === +String(dataKey).replace("labelType_", "")
          );
          if (!currentType) return null;

          const labels = currentType.labels.map((label) => (
            <Tag color={currentType.bgColour} key={label.id} variant="solid">
              <span style={{ color: currentType.fgColour }}>{label.viewName}</span>
            </Tag>
          ));

          return (
            <Tooltip placement="left" title={currentType.labels.map(({ viewName }) => viewName).join(", ")}>
              <Space size={2}>{labels}</Space>
            </Tooltip>
          );
        },
        filters: getFiltersForTable(type.labels, "viewName", "id"),
      }));
  };

  const columns = useMemo<TableColumnProps<API_ViewInformation>[]>(() => {
    const isGrow = viewsStore.viewInformation.metadata?.length >= 4 ? 1 : 0;
    const cols: TableColumnProps<API_ViewInformation>[] = [];

    if (view.showFilename) {
      cols.push({
        dataKey: "name",
        width: 230,
        label: t("Name"),
        flexShrink: 0,
      });
    }

    if (view.showDescription) {
      cols.push({
        dataKey: "description",
        width: 250,
        label: t("Description"),
        flexShrink: 0,
      });
    }

    if (view.showIndex) {
      cols.push({
        dataKey: "index",
        width: 50,
        label: t("Index"),
        flexShrink: 0,
      });
    }

    if (view.showType) {
      cols.push({
        dataKey: "type",
        width: 70,
        label: t("Type"),
        flexShrink: 0,
        filters: viewsStore.filterableFileTypes.map((i) => ({ value: i, text: i })),
      });
    }

    if (view.showLastChange) {
      cols.push({
        dataKey: "created",
        width: 170,
        label: t("Last change"),
        initialSortOrder: "DESC",
        cellRenderer: ({ cellData }) => dayjs(+cellData).format("lll"),
        flexShrink: 0,
        customFilterRender: (props) => (
          <ViewDateFilter
            {...props}
            showYoungerThanDays={true}
            onYoungerThanDaysChange={handleYoungerThanDaysChange}
            youngerThanDaysValue={activeFilters.youngerThanDays?.[0]}
          />
        ),
      });
    }

    const labelTypeColumns = getTypeLabelColumns(viewsStore.viewInformation.typesWithLabels, isGrow);
    if (labelTypeColumns.length) {
      cols.push(...labelTypeColumns);
    }

    viewsStore.viewInformation.metadata?.forEach((meta: API_Metadata) => {
      if (meta.show) {
        cols.push(createMetadataColumn(meta, isGrow));
      }
    });

    cols.push({
      dataKey: "id",
      label: t("Actions"),
      disableSort: true,
      width: 120,
      flexGrow: 0,
      cellRenderer: ({ rowData }) => (
        <Row justify="end" align="middle">
          {rowData.fileActions?.download || rowData.fileActions?.downloadWithWatermark ? (
            <DirectDownload module="VIEW" fileId={rowData.id} />
          ) : null}
          <ViewsTableRowActions data={rowData}>
            <div className="docs-dropdown-trigger" onClick={(e) => e.stopPropagation()}>
              <FontAwesomeIcon className="email-action-icon" icon={["far", "ellipsis-v"]} fixedWidth />
            </div>
          </ViewsTableRowActions>
        </Row>
      ),
    });

    if (view.showStatus) {
      cols.push({
        dataKey: "fileState",
        width: 100,
        disableSort: true,
        label: t("File Status"),
        flexShrink: 0,
        filters: viewsStore.filterableStatus.map((item) => ({
          value: item.toString(),
          text: <div className={`status-marker for-filter marker-${item}`} />,
        })),
        cellRenderer: ({ cellData }) => <div className={`status-marker marker-${cellData}`} />,
      });
    }

    return cols;
  }, [
    view,
    handleYoungerThanDaysChange,
    viewsStore.filterableFileTypes,
    viewsStore.filterableStatus,
    activeFilters.youngerThanDays,
    viewsStore.viewInformation.typesWithLabels,
    viewsStore.viewInformation.metadata,
  ]);

  useEffect(() => {
    const listener = (e: CustomEvent<API_ViewRequest["metadata"]>) => onExternalFilterViewEvent(e);
    document.addEventListener("filter_view", listener as EventListener);

    return () => {
      document.removeEventListener("filter_view", listener as EventListener);
    };
  }, [view]);

  useEffect(() => {
    // fetchData will trigger on filters and sort change
    fetchData();
  }, [sortBy, sortDirection, activeFilters, viewAll, youngerThanDays]);

  if (!viewsStore.currentViewId) {
    return null;
  }

  return (
    <>
      <ViewFilterSearchBar onSearch={fetchData} onChange={onFilterChange} />
      <Row justify="space-between" align="middle" className="mt-1 mb-md">
        <ViewsActionsMenu
          fileIds={selectedFileIds}
          files={data.filter((file) => selectedFileIds.includes(file?.id))}
          viewExportInfo={getRequestFormBody()}
          fetch={fetchData}
          resetSelection={resetSelection}
        />
        <div>
          <div className="mr-10" style={{ display: "inline-block" }}>
            <SaveFiltersButton
              isActive={viewsStore.fromSavedFilter}
              viewId={viewsStore.currentViewId}
              onFilter={handleExternalFilter}
              activeFilters={activeFilters}
            />
          </div>
          <div className="mr-10" style={{ display: "inline-block" }}>
            <ViewSettings
              viewId={viewsStore.currentViewId}
              reFetch={handleUpdate}
              view={view}
              viewInformation={viewsStore.viewInformation}
            />
          </div>
          {view.viewRight === "WRITE" && projectsDataStore.functions.includes("AUTOMATICDIRMAPPING") && (
            <Button faIcon="plus" type="primary" onClick={openViewsFileUpload}>
              {t("New file")}
            </Button>
          )}
        </div>
      </Row>
      <DocumentsDnD uploadMode="VIEW" disabled={view.viewRight === "READONLY"}>
        <ScrollableTable<API_ViewInformation>
          ref={table}
          selectAllRequest={selectAll}
          alwaysCallSelectAll
          loading={isLoading}
          rowKey="id"
          stretchHeight={true}
          data={viewsStore.files.slice()}
          highlightedRowId={[highlightedFileId]}
          columns={columns}
          sortBy={sortBy as keyof API_ViewInformation}
          sortDirection={sortDirection}
          onSort={handleSort}
          onFilter={handleFilter}
          selectedRowKeys={selectedFileIds}
          onSelectRows={handleSelectRows}
          onRowClick={handleRowClick}
          loadMore={viewsStore.loadMoreData}
          rowCount={viewsStore.total}
          rowClassName={getRowClassName}
          resizeableHeaders={"VIEW_" + viewsStore.currentViewId}
        />
      </DocumentsDnD>
    </>
  );
});
